import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ChevronDown, Check, LoaderCircle, Dumbbell, EyeOff, Eye } from 'lucide-react';
import ForgotPasswordModal from '../../components/ForgotPasswordModal';
import api from '../../services/ApiService';
import { redirectByRole } from '../../constant/redirect';
import logo from '../../assets/images/logo.jpg'
import { ROUTES } from '../../constant/routePaths';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('Admin');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password.');
      return;
    }  
    let data = {
      "identifier": email,
      "password": password
    }

    try {
      setIsLoading(true);
      const response = await api.post("auth/login", data);
      const result = response?.data;
      console.log(response);

      if (response.status === 200) {
        console.log(result?.user?.is_temporary_password);
        localStorage.removeItem("authToken");
        localStorage.setItem("temptoken", result?.token);
        localStorage.setItem("user", JSON.stringify(result?.user));
        if (result?.user?.is_temporary_password) {
          toast.success("Login successful! Reset your Password with old Password")
          navigate(`${ROUTES.RESETPASSWORD}/${result?.user?.role_id}`);
          return;
        }
        localStorage.setItem("authToken", result?.token);
        redirectByRole(result?.user?.role_id, navigate, { message: result?.message || "Login successful!" });

      }
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <motion.div
        className="w-full max-w-6xl mx-auto flex flex-col md:flex-row bg-white shadow-2xl rounded-2xl overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Left Panel: Form */}
        <div className="w-full md:w-1/2 p-8 sm:p-12 flex flex-col justify-center">
          <div className="max-w-md mx-auto w-full">
            <div className="flex items-center gap-3 mb-6">
              <img src={logo} alt="Logo" width={150} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2 font-display">Welcome Back!</h2>
            <p className="text-gray-500 mb-8">Login to access your dashboard.</p>

            <form onSubmit={handleLogin} className="space-y-6">
              {/* Email Input */}
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  // type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red/50 focus:border-brand-red outline-none transition-all"
                  required
                />
              </div>

              {/* Password Input */}
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red/50 focus:border-brand-red outline-none transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-gray-600 cursor-pointer">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={() => setRememberMe(!rememberMe)}
                      className="peer appearance-none h-5 w-5 border border-gray-400 rounded-md checked:bg-brand-red checked:border-transparent focus:outline-none"
                    />
                    <Check className={`absolute left-0.5 top-0.5 h-4 w-4 text-white transition-opacity ${rememberMe ? 'opacity-100' : 'opacity-0'}`} />
                  </div>
                  Remember Me
                </label>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="text-sm font-medium text-brand-red hover:underline focus:outline-none"
                >
                  Forgot Password?
                </button>
              </div>

              <motion.button
                whileTap={{ scale: 0.95 }}
                type="submit"
                disabled={isLoading}
                className="w-full bg-brand-red text-white py-3 rounded-lg font-semibold text-lg hover:bg-red-600 focus:outline-none focus:ring-4 focus:ring-brand-red/50 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <LoaderCircle className="animate-spin h-6 w-6" />
                    Authenticating...
                  </>
                ) : (
                  'Login'
                )}
              </motion.button>
            </form>
          </div>
        </div>

        {/* Right Panel: Image */}
        <div className="hidden md:block md:w-1/2">
          <div className="h-full w-full bg-cover bg-center" style={{ backgroundImage: "url('public/gym-image.jpg')" }}>
            <div className="h-full w-full bg-black/50"></div>
          </div>
        </div>
      </motion.div>
      <ForgotPasswordModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default LoginPage;
